/**
 * dataTableCardDirective
 *
 * @author Dec Norton <decnorton@gmail.com>
 */

import { get, isFunction } from 'lodash-es';

dataTableCardDirective.$inject = ['$permissions'];

export function dataTableCardDirective($permissions) {
    return {
        transclude: {
            menu: '?appDataTableCardMenu',
        },
        //language=HTML
        template: `
            <md-card class="app-table-card"
                     ng-class="{ loading: loading }"
                     layout="column">
                <md-card-title>
                    <h2>
                        {{ config.title }}

                        <small ng-if="config.subtitle || paginationTotal !== undefined"
                               app-visible="!loading">

                            <ng-template ng-if="config.subtitle">{{ config.subtitle }}</ng-template>

                            <ng-template ng-if="config.subtitle && paginationTotal">&bull;</ng-template>

                            <ng-template ng-if="paginationTotal">
                                {{ paginationTotal }} total
                            </ng-template>
                        </small>
                    </h2>

                    <div class="float-right row">
                        <ng-template ng-transclude="menu"></ng-template>

                        <app-search
                            ng-if="showSearch"
                            is-showing="true"
                            on-search="onSearch(query)"
                            query="searchQuery">
                        </app-search>

                        <md-button class="md-icon-button"
                                   aria-label="New"
                                   ng-click="onNewItem({ $event: $event })"
                                   data-testid="icon:add"
                                   ng-if="ui.shouldShowNewItemButton">
                            <md-icon>add</md-icon>
                            <md-tooltip>New</md-tooltip>
                        </md-button>
                    </div>
                </md-card-title>

                <md-card-content>
                    <app-data-table items="items"
                                    track-by="{{ trackBy }}"
                                    class-names="classNames"
                                    columns="columns"
                                    formatters="formatters"
                                    headings="headings"
                                    loading="loading"
                                    row-class="rowClass"
                                    on-item-click="onItemClicked($event, item)"
                                    widths="widths"
                                    config="config"
                                    selectable="selectable"
                                    on-action-click="onActionClicked($event, item, action)"
                                    actions="actions">
                    </app-data-table>
                </md-card-content>

                <md-card-actions>
                    <app-pagination
                        ng-if="pagination"
                        paginator="paginator"
                        page-count="paginationPageCount"
                        current="paginationCurrentPage"
                        change="onPageChanged(newPage)">
                    </app-pagination>
                </md-card-actions>
            </md-card>
        `,
        scope: {
            permissions: '=?permissions',
            classNames: '=?classNames',
            columns: '=columns',
            config: '=config',
            formatters: '=formatters',
            headings: '=headings',
            items: '=items',
            loading: '=loading',
            onItemClick: '&onItemClick',
            onNewItem: '&onNewItem',
            pagination: '=?pagination',
            paginator: '=?paginator',
            paginationCurrentPage: '=paginationCurrent',
            paginationOnChange: '&paginationOnChange',
            paginationPageCount: '=paginationPageCount',
            paginationTotal: '=paginationTotal',
            rowClass: '=?rowClass',
            search: '&?search',
            searchQuery: '=searchQuery',
            selectable: '=?selectable',
            title: '=title',
            trackBy: '@trackBy',
            widths: '=?widths',
            actions: '=?actions',
            onActionClick: '&onActionClick'
        },
        link: function (scope, element, attrs) {
            scope.ui = {
                get shouldShowNewItemButton() {
                    const createPermission = get(scope.permissions, 'create');

                    if (createPermission && !$permissions.can(createPermission)) {
                        return false;
                    }

                    return !!attrs.onNewItem && get(scope.config, 'hideNewItemButton') !== true;
                },
            };

            scope.pagination = angular.isDefined(scope.pagination) ? !!scope.pagination : true;

            scope.onItemClicked = onItemClicked;
            scope.onActionClicked = onActionClicked;
            scope.onPageChanged = onPageChanged;
            scope.onSearch = onSearch;

            scope.showSearch = angular.isDefined(scope.search);

            /**
             * Pass through the pagination directive callback.
             * @param  {number} newPage The page to change to.
             * @return {void}
             */
            function onPageChanged(newPage) {
                if (isFunction(scope.paginationOnChange)) {
                    scope.paginationOnChange({
                        newPage: newPage,
                    });
                }
            }

            /**
             * Pass through the data table onItemClick callback.
             * @param  {MouseEvent} $event
             * @param  {Object}     item   The item that was clicked.
             * @return {void}
             */
            function onItemClicked($event, item) {
                if (isFunction(scope.onItemClick)) {
                    scope.onItemClick({
                        $event,
                        item,
                    });
                }
            }

            /**
             * Pass through the data table onActionClick callback.
             * @param  {MouseEvent} $event
             * @param  {Object}     item   The item that was clicked.
             * @return {void}
             */
            function onActionClicked($event, item, action) {
                if (isFunction(scope.onActionClick)) {
                    scope.onActionClick({
                        $event,
                        item,
                        action
                    });
                }
            }

            /**
             * Pass through the search directive callback.
             * @param  {string} query The search query.
             * @return {void}
             */
            function onSearch(query) {
                if (isFunction(scope.search)) {
                    scope.search({
                        query: query,
                    });
                }
            }
        },
    };
}
